import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDatepickerModule, NgbDateStruct, NgbDropdownModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { PerfectScrollbarService } from 'src/app/_services/perfect-scrollbar.service';
import { environment } from '../../../environments/environment';
import { ChevronDownIconComponent } from '../../shared/components/icons';
import { PopupManager, PopupType } from '../popup-manager';
import { ApiControllerService } from '../api-controller-service';
import { AlertService } from 'src/app/_services/alert.service';
import { ExportService } from 'src/app/_services/export.service';
import { IpWhitelistDialogComponent, IPWhitelistFormData, DialogMode } from './ip-whitelist-dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { parseHttpErrorMessage } from 'src/app/_helpers/http-error.utils';
import { Role } from 'src/app/_helpers/auth.constants';
import { AuthService } from 'src/app/_services/auth.service';
const { defaultPageSizeArr, defaultPageSize, maxWaitingTimeForDownload } = environment;

interface IPWhitelist {
  id: string;
  ipAddress: string;
  name?: string;
  status: Status;
  merchantId: string;
  merchantName?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  version: number;
}

export enum FilterField {
  MERCHANT_ID = 'Merchant ID',
  MERCHANT_NAME = 'Merchant Name',
  DATE = 'Date',
  STATUS = 'Status'
}

enum FilterType {
  DATE_RANGE = 'date-range',
  DROPDOWN = 'dropdown',
  TEXT_INPUT = 'text-input'
}

class DropdownItem {
  label: string;
  value: any;
  visible: boolean = true;
  selected: boolean = false;
  children?: DropdownItem[];
  expanded: boolean = false;
  
  constructor(label: string, value: any, children?: DropdownItem[]) {
    this.label = label;
    this.value = value;
    this.children = children;
  }
  
  hasChildren(): boolean {
    return this.children !== undefined && this.children.length > 0;
  }
  
  getAllDescendants(): DropdownItem[] {
    if (!this.hasChildren()) {
      return [];
    }
    
    let descendants: DropdownItem[] = [];
    for (const child of this.children!) {
      descendants.push(child, ...child.getAllDescendants());
    }
    return descendants;
  }
  
  getAllLeafNodes(): DropdownItem[] {
    if (!this.hasChildren()) {
      return [this];
    }
    
    let leafNodes: DropdownItem[] = [];
    for (const child of this.children!) {
      leafNodes.push(...child.getAllLeafNodes());
    }
    return leafNodes;
  }
}

enum DateRangeType {
  TODAY = 'today',
  LAST_7_DAYS = 'last7days',
  LAST_14_DAYS = 'last14days',
  LAST_30_DAYS = 'last30days',
  LAST_3_MONTHS = 'last3months',
  QUARTER_TO_DATE = 'quartertodate',
  YEAR_TO_DATE = 'yeartodate'
}

export enum Status {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED'
}

@Component({
  selector: 'app-ip-whitelisting',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AgGridAngular,
    NgbPaginationModule,
    NgbDropdownModule,
    NgbDatepickerModule,
    ChevronDownIconComponent,
    IpWhitelistDialogComponent
  ],
  templateUrl: './ip-whitelisting.component.html',
  styleUrls: ['./ip-whitelisting.component.css']
})
export class IpWhitelistingComponent implements OnInit {
  constructor(
    private readonly apiControllerService: ApiControllerService,
    private readonly authService: AuthService,
    private readonly calendar: NgbCalendar,
    private readonly cdr: ChangeDetectorRef,
    private readonly alertService: AlertService,
    private readonly exportService: ExportService,
    private readonly perfectScrollbarService: PerfectScrollbarService,
    private readonly sanitizer: DomSanitizer
  ) {}

  role: string = '';
  Role = Role;

  ngOnInit() {
    this.perfectScrollbarService.initializeScrollableList();
    this.columnDefs = this.columnDefs.map((c, i) => ({
      ...c,
      colId: (c as any).colId || c.headerName || `col_${i}`
    }));

    this.role = this.authService.getRole();
    // Non-admin auto-loads; admin waits for merchant filter
    if (this.role !== Role.PAY10_ADMIN && this.gridApi) {
      this.fetchIPWhitelist();
    }
  }

  /** True when admin has applied a Merchant Name or Merchant ID filter. */
  get hasMerchantFilter(): boolean {
    return this.filters.has(FilterField.MERCHANT_ID) || this.filters.has(FilterField.MERCHANT_NAME);
  }

  /** Disable History/Export when admin has no merchant filter or when no results exist. */
  get isAdminGated(): boolean {
    if (this.role === Role.PAY10_ADMIN) {
      return !this.hasMerchantFilter || this.totalItems === 0;
    }
    return this.totalItems === 0;
  }

  ngAfterViewInit() {
    this.initializeDualCalendars();
  }

  readonly FilterType = FilterType;
  private readonly popupManager: PopupManager = new PopupManager();
  
  // IP Whitelist Dialog state
  isIpDialogVisible = false;
  ipDialogMode: DialogMode = 'add';
  selectedIpData: IPWhitelistFormData | null = null;
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Element;
    const currentPopup = this.popupManager.getCurrent();
    
    // Close action menu if clicking outside
    if (!target.closest('.actions-dropdown')) {
      this.openedActionRowId = null;
    }
    
    switch (currentPopup) {
      case PopupType.CUSTOMIZE: {
        const customizeDropdown = target.closest('.customize-dropdown');
        if (!customizeDropdown) {
          this.popupManager.closeAll();
        }
        break;
      }
      case PopupType.DATE_PICKER: {
        const datePickerArea = target.closest('.date-range-picker');
        if (!datePickerArea) {
          this.popupManager.closeAll();
        }
        break;
      }
      default:
        break;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    this.popupManager.closeAll();
  }

  get noRowsTemplate(): string {
    if (this.filters.size > 0) {
      // When filters are applied
      return `
        <div class="no-results-overlay">
          <img src="assets/img/start.svg" alt="No results" class="no-results-icon" />
          <div class="no-results-title">No Results Found</div>
          <div class="no-results-description">There are no IP addresses that match your search.</div>
        </div>
      `;
    } else {
      // When no filters are applied (empty state)
      return `
        <div class="no-results-overlay">
          <img src="assets/img/start.svg" alt="No results" class="no-results-icon" />
          <div class="no-results-title">Add Your First IP Address</div>
          <div class="no-results-description">Ready to get started? Let's add your first IP Address and unlock the full potential of our service!</div>
        </div>
      `;
    }
  }

  private gridApi!: GridApi<IPWhitelist>;
  onGridReady(params: GridReadyEvent<IPWhitelist>) {
    this.gridApi = params.api;
    //It should be non admin or Admin must apply a merchant filter before data is fetched
    if (this.role !== Role.PAY10_ADMIN || this.hasMerchantFilter) {
      this.fetchIPWhitelist();
    }
    
    setTimeout(() => {
      this.perfectScrollbarService.initializeAgGrid();
    }, 100);
  }

  openedActionRowId: string | null = null;
  actionMenuPosition = { top: 0, left: 0 };
  
  toggleActionMenu(rowId: string, event: Event) {
    event.stopPropagation();
    
    if (this.openedActionRowId === rowId) {
      this.openedActionRowId = null;
    } else {
      this.openedActionRowId = rowId;
      
      // Calculate position for dropdown menu
      const button = (event.target as HTMLElement).closest('.actions-trigger');
      if (button) {
        const rect = button.getBoundingClientRect();
        this.actionMenuPosition = {
          top: rect.bottom + 4,
          left: rect.right - 160 // 160px is the min-width of the menu
        };
      }
    }
  }
  
  isActionMenuOpen(rowId: string): boolean {
    return this.openedActionRowId === rowId;
  }
  
  get selectedRow(): IPWhitelist | undefined {
    return this.ipWhitelistData.find(row => row.id === this.openedActionRowId);
  }
  
  onEditAction() {
    const row = this.selectedRow;
    console.debug("onEditAction:", row?.id);
    if (!row) return;
    this.openedActionRowId = null;
    console.log('Edit IP Whitelist:', row);
    
    // Open dialog in edit mode with current IP data
    this.ipDialogMode = 'edit';
    this.selectedIpData = {
      id: row.id,
      ipAddress: row.ipAddress,
      name: row.name,
      status: row.status,
      merchantId: row.merchantId,
      merchantName: row.merchantName,
      version: row.version
    };
    this.isIpDialogVisible = true;
  }
  
  onDisableAction() {
    const row = this.selectedRow;
    console.debug("onDisableAction", row?.id);
    if (!row) return;
    this.openedActionRowId = null;
    console.log('Disable IP Whitelist:', row);
    this.alertService.customInfoPopup({
      icon: 'assets/img/Info.svg',
      title: 'Disable IP Address?',
      description: `This action will disable the selected IP address. Are you sure you want to continue?`,
      primaryButton: {
        text: 'Continue',
        action: () => {
          this.disableIPAddress(row);
        }
      },
      secondaryButton: {
        text: 'Cancel'
      }
    });
  }

  private disableIPAddress(ipWhitelistData: IPWhitelist) {
    ipWhitelistData.status = Status.DISABLED;
    this.apiControllerService.updateIPWhitelist(ipWhitelistData).subscribe({
      next: () => {
        this.fetchIPWhitelist();
      },
      error: (error: HttpErrorResponse) => {
        console.error("disableIPAddress failed:", error);
        this.alertService.customInfoPopup({
          icon: 'assets/img/failure.svg',
          title: 'Disable IP Address Failed',
          description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
          primaryButton: {
            text: 'OK'
          }
        });
      }
    });
  }
  
  openAddIpDialog() {
    this.ipDialogMode = 'add';
    this.selectedIpData = null;
    this.isIpDialogVisible = true;
  }
  
  handleIpDialogSuccess() {
    console.debug('IP Dialog submitted:', this.ipDialogMode);
    const title = this.ipDialogMode === 'add' ? 'Whitelisted IP Added' : 'Whitelisted IP Updated';
    const description = this.ipDialogMode === 'add' ? 'The IP has been successfully added.' : 'The IP has been successfully updated.';
    this.alertService.customInfoPopup({
      icon: 'assets/img/Tick.svg',
      title: title,
      description: description,
      primaryButton: {
        text: 'Continue',
        action: () => {
          this.handleIpDialogClose();
          this.fetchIPWhitelist();
        }
      }
    });
  }

  handleIpDialogFailed(errorMessage: string) {
    const title = this.ipDialogMode === 'add' ? 'Whitelisted IP Added Failed' : 'Whitelisted IP Updated Failed';
    this.alertService.customInfoPopup({
      icon: 'assets/img/failure.svg',
      title: title,
      description: errorMessage,
      primaryButton: {
        text: 'OK',
        action: () => {
          this.handleIpDialogClose();
          this.fetchIPWhitelist();
        }
      }
    });
  }
  
  handleIpDialogClose() {
    this.isIpDialogVisible = false;
    this.selectedIpData = null;
  }
  
  getRowId = (params: { data: IPWhitelist }) => String(params.data?.id);
  
  defaultColDef: ColDef = {
    filter: false,
    sortable: false,
    resizable: true,
    autoHeight: true,
    suppressHeaderMenuButton: true,
    flex: 1
  };
  
  columnDefs: ColDef[] = [
    { field: 'ipAddress', sortable: false, lockVisible: true, headerName: 'IP Address', resizable: true },
    { field: 'status', sortable: false, lockVisible: true, headerName: 'Status', resizable: true },
    { field: 'createdAt', sortable: false, lockVisible: true, headerName: 'Added On', resizable: true },
    { field: 'createdBy', sortable: false, lockVisible: true, headerName: 'Added By', resizable: true },
    { field: 'name', sortable: false, lockVisible: true, headerName: 'Tag/Nickname', resizable: true },
    { field: 'updatedAt', sortable: false, lockVisible: true, headerName: 'Last Updated On', resizable: true },
    {
      headerName: 'Actions',
      colId: 'actions',
      width: 90,
      lockVisible: true,
      pinned: 'right',
      sortable: false, 
      resizable: false,
      cellRenderer: (params: any) => {
        const container = document.createElement('div');
        container.className = 'actions-cell';
        container.innerHTML = `
          <div class="actions-dropdown" data-row-id="${params.data.id}">
            <button class="actions-trigger" type="button">
              <span class="dots"></span>
            </button>
          </div>
        `;
        
        const button = container.querySelector('.actions-trigger');
        button?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleActionMenu(params.data.id, e);
        });
        
        return container;
      }
    }
  ]

  ipWhitelistData: IPWhitelist[] = [];
  totalItems: number = 0;
  totalItemsWithoutFilters: number = 0;
  isViewOnlyAccess: boolean = false; // Set to true to show access notice

  // --- History Panel State ---
  showHistoryPanel: boolean = false;
  historyItems: any[] = [];
  historyPageNumber: number = 0; // zero-based to match backend
  historyPageSize: number = 20;
  historyTotalItems: number = 0;
  historyLoading: boolean = false;
  historyHasMore: boolean = true;
  @ViewChild('historyScrollContainer') historyScrollContainer!: ElementRef<HTMLDivElement>;

  fetchIPWhitelist() {
    // Admin without a merchant filter: show empty grid (overlay handled by noRowsTemplate)
    const shouldGateFetch = this.role === Role.PAY10_ADMIN && !this.hasMerchantFilter;
    if (shouldGateFetch) {
      this.ipWhitelistData = [];
      this.totalItems = 0;
      if (this.gridApi) {
        this.gridApi.setGridOption('overlayNoRowsTemplate', this.noRowsTemplate);
        this.gridApi.showNoRowsOverlay();
      }
      return;
    }

    // For admin: resolve merchant name/id to a real merchantId before fetching
    const merchantNameValue = this.filters.get(FilterField.MERCHANT_NAME);
    const merchantIdValue = this.filters.get(FilterField.MERCHANT_ID);
    const merchantQuery = merchantNameValue || merchantIdValue;

    if (this.role === Role.PAY10_ADMIN && merchantQuery) {
      this.apiControllerService.findMerchant(merchantQuery).subscribe({
        next: (resp: any) => {
          const data = resp?.response?.data ?? resp?.data ?? null;
          if (data && data.merchantId) {
            this.filters.delete(FilterField.MERCHANT_NAME);
            this.filters.set(FilterField.MERCHANT_ID, data.merchantId);
            this.doFetchIPWhitelist();
          } else {
            this.ipWhitelistData = [];
            this.totalItems = 0;
            if (this.gridApi) { this.gridApi.showNoRowsOverlay(); }
          }
        },
        error: () => {
          this.ipWhitelistData = [];
          this.totalItems = 0;
          if (this.gridApi) { this.gridApi.showNoRowsOverlay(); }
        }
      });
      return;
    }

    this.doFetchIPWhitelist();
  }

  private doFetchIPWhitelist() {
    this.apiControllerService.getIPWhitelist(this.rowsPerPage, this.currentPage - 1, this.filters).subscribe({
      next: (resp: any) => {
        this.ipWhitelistData = resp.content;
        this.totalItems = resp.totalElements;
        if(this.filters.size === 0) {
            this.totalItemsWithoutFilters = this.totalItems;
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error("fetchIPWhitelist failed:", error);
        if(error.status !== 401) {
          this.alertService.customInfoPopup({
            icon: 'assets/img/failure.svg',
            title: 'IP whitelist fetching Failed',
            description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
            primaryButton: {
              text: 'OK'
            }}
          );
        }
      }
    });
  }

  selectedIPWhitelist: IPWhitelist | null = null;

  readonly pageSizeOptions: number[] = defaultPageSizeArr;
  currentPage: number = 1;
  rowsPerPage: number = defaultPageSize;

  filters: Map<FilterField, any> = new Map();

  readonly filterFieldOptions: FilterField[] = [
    FilterField.MERCHANT_NAME,
    FilterField.MERCHANT_ID,
    FilterField.STATUS
  ];

  filterField: FilterField = this.filterFieldOptions[0];
  
  onFilterDropdownClick(dropdown: any, event: Event) {
    this.popupManager.closeAll();
    setTimeout(() => {
      const isOpen = dropdown.isOpen();
      if (isOpen) {
        this.popupManager.toggle(PopupType.FILTER_FIELD);
      }
    }, 0);
  }

  onFilterValueDropdownOpen() {
    setTimeout(() => {
      this.perfectScrollbarService.initializeScrollableList(['.filter-value-area .recursive-menu-scrollable-list']);
    });
  }

  putFilterField(filterKey: FilterField) {
    this.filterField = filterKey;
    this.initFilterValues();
    this.customizeSearch = '';
    this.searchInDropdownMenu();
    
    if (filterKey === FilterField.DATE) {
      setTimeout(() => {
        this.initializeDualCalendars();
      }, 0);
    }
  }

  private initFilterValues() {
    this.dateFromFilter = null;
    this.dateToFilter = null;
    this.selectedDateRange = '';
    
    const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
    allItems.forEach(option => {
      option.selected = false;
      option.visible = true;
      option.expanded = false;
    });
    
    this.dropdownFilterValues.clear();
    this.textInputFilter = '';
    this.showSelectedMenuItem = false;
    this.searchTextInDropdownMenu = '';
  }

  get currentFilterType(): FilterType {
    switch (this.filterField) {
      case FilterField.STATUS:
        return FilterType.DROPDOWN;
      case FilterField.MERCHANT_ID:
      case FilterField.MERCHANT_NAME:
      default:
        return FilterType.TEXT_INPUT;
    }
  }

  dateFromFilter: NgbDate | null = null;
  dateToFilter: NgbDate | null = null;
  dropdownFilterValues = new Set<string>();
  textInputFilter: string = '';

  selectedDateRange: string = '';
  leftCalendarDate: NgbDate | null = null;
  rightCalendarDate: NgbDate | null = null;
  leftCalendarMinDate: NgbDate | null = null;
  minSelectableDate!: NgbDate;
  maxSelectableDate!: NgbDate;
  @ViewChild('leftDatePicker') leftDatePicker!: NgbDatepicker;
  @ViewChild('rightDatePicker') rightDatePicker!: NgbDatepicker;
  
  private initializeDualCalendars() {
    const today = this.calendar.getToday();
    const previousMonth = this.calendar.getPrev(today, 'm', 1);
    
    this.minSelectableDate = this.calendar.getPrev(today, 'y', 3);
    this.maxSelectableDate = today;
    
    this.leftCalendarDate = previousMonth;
    this.rightCalendarDate = today;
    this.leftCalendarMinDate = this.minSelectableDate;
    
    if (this.leftDatePicker && this.leftCalendarDate) {
      this.leftDatePicker.navigateTo(this.leftCalendarDate);
    }
    if (this.rightDatePicker && this.rightCalendarDate) {
      this.rightDatePicker.navigateTo(this.rightCalendarDate);
    }
    this.cdr.detectChanges();
  }

  get showDatePicker(): boolean {
    return this.popupManager.isOpen(PopupType.DATE_PICKER);
  }
  
  toggleDatePicker() {
    this.popupManager.toggle(PopupType.DATE_PICKER);
  }

  readonly dateRangeOptions: DropdownItem[] = [
    new DropdownItem('Today', DateRangeType.TODAY),
    new DropdownItem('Last 7 days', DateRangeType.LAST_7_DAYS),
    new DropdownItem('Last 14 days', DateRangeType.LAST_14_DAYS),
    new DropdownItem('Last 30 days', DateRangeType.LAST_30_DAYS),
    new DropdownItem('Last 3 Months', DateRangeType.LAST_3_MONTHS),
    new DropdownItem('Quarter to date', DateRangeType.QUARTER_TO_DATE),
    new DropdownItem('Year to date', DateRangeType.YEAR_TO_DATE)
  ];
  
  readonly statusOptions: DropdownItem[] = [
    new DropdownItem('Active', Status.ACTIVE),
    new DropdownItem('Disabled', Status.DISABLED)
  ];

  get currentDropdownOptions(): DropdownItem[] {
    switch (this.filterField) {
      case FilterField.STATUS:
        return this.statusOptions;
      default:
        return [];
    }
  }

  private getAllDropdownItems(items: DropdownItem[]): DropdownItem[] {
    let allItems: DropdownItem[] = [];
    for (const item of items) {
      allItems.push(item);
      if (item.hasChildren()) {
        allItems.push(...this.getAllDropdownItems(item.children!));
      }
    }
    return allItems;
  }

  toggleItemExpansion(item: DropdownItem) {
    item.expanded = !item.expanded;
  }

  private selectAllChildren(item: DropdownItem, selected: boolean) {
    if (item.hasChildren()) {
      item.children!.forEach(child => {
        child.selected = selected;
        if (child.value !== undefined) {
          if (selected) {
            this.dropdownFilterValues.add(child.value);
          } else {
            this.dropdownFilterValues.delete(child.value);
          }
        }
        this.selectAllChildren(child, selected);
      });
    }
  }

  private updateParentSelection(allOptions: DropdownItem[]) {
    const processItem = (item: DropdownItem) => {
      if (item.hasChildren()) {
        item.children!.forEach(child => processItem(child));
        
        const allChildrenSelected = item.children!.every(child => child.selected);
        const noChildrenSelected = item.children!.every(child => !child.selected);
        
        if (allChildrenSelected) {
          item.selected = true;
          if (item.value !== undefined) {
            this.dropdownFilterValues.add(item.value);
          }
        } else if (noChildrenSelected) {
          item.selected = false;
          if (item.value !== undefined) {
            this.dropdownFilterValues.delete(item.value);
          }
        }
      }
    };
    
    allOptions.forEach(item => processItem(item));
  }

  toggleDropdownFilterValue(item: DropdownItem, ev: Event) {
    item.selected = !item.selected;
    
    if (item.value !== undefined) {
      if (item.selected) {
        this.dropdownFilterValues.add(item.value);
      } else {
        this.dropdownFilterValues.delete(item.value);
      }
    }
    
    if (item.hasChildren()) {
      this.selectAllChildren(item, item.selected);
    }
    
    this.updateParentSelection(this.currentDropdownOptions);
    
    this.applyCurrentFilter();
  }

  searchTextInDropdownMenu: string = '';
  
  searchInDropdownMenu() {
    const searchTerm = (this.searchTextInDropdownMenu || '').trim().toLowerCase();
    
    const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
    allItems.forEach(item => {
      if (searchTerm === '') {
        item.visible = true;
      } else {
        item.visible = item.label.toLowerCase().includes(searchTerm);
      }
    });
    
    if (searchTerm !== '') {
      this.expandParentsOfVisibleItems(this.currentDropdownOptions);
    }
  }

  private expandParentsOfVisibleItems(items: DropdownItem[]) {
    for (const item of items) {
      if (item.hasChildren()) {
        const hasVisibleChild = item.children!.some(child => 
          child.visible || (child.hasChildren() && this.hasVisibleDescendant(child))
        );
        if (hasVisibleChild) {
          item.visible = true;
          item.expanded = true;
        }
        this.expandParentsOfVisibleItems(item.children!);
      }
    }
  }

  private hasVisibleDescendant(item: DropdownItem): boolean {
    if (!item.hasChildren()) {
      return false;
    }
    return item.children!.some(child => 
      child.visible || this.hasVisibleDescendant(child)
    );
  }

  selectAllMenuItems() {
    const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
    const allVisibleItems = allItems.filter(item => item.visible);
    
    if(this.dropdownFilterValues.size < allVisibleItems.length) {
      allVisibleItems.forEach(item => {
        this.dropdownFilterValues.add(item.value);
      });
      this.applyCurrentFilter();
    }
    allVisibleItems.forEach(item => {
      item.selected = true;
    });
  }
  
  deselectAllMenuItems() {
    const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
    const allVisibleItems = allItems.filter(item => item.visible);
    
    if(this.dropdownFilterValues.size > 0) {
      allVisibleItems.forEach(item => {
        this.dropdownFilterValues.delete(item.value);
      });
      this.applyCurrentFilter();
    }
    allVisibleItems.forEach(item => {
      item.selected = false;
    });
  }

  showSelectedMenuItem: boolean = false;
  
  toggleShowSelectedMenuItem() {
    this.showSelectedMenuItem = !this.showSelectedMenuItem;
    const allItems = this.getAllDropdownItems(this.currentDropdownOptions);
    
    if(this.showSelectedMenuItem) {
      allItems.forEach(item => {
        item.visible = item.selected;
      });
      this.expandParentsOfSelectedItems(this.currentDropdownOptions);
    } else {
      allItems.forEach(item => {
        item.visible = true;
      });
    }
  }

  private expandParentsOfSelectedItems(items: DropdownItem[]) {
    for (const item of items) {
      if (item.hasChildren()) {
        const hasSelectedChild = item.children!.some(child => 
          child.selected || (child.hasChildren() && this.hasSelectedDescendant(child))
        );
        if (hasSelectedChild) {
          item.visible = true;
          item.expanded = true;
        }
        this.expandParentsOfSelectedItems(item.children!);
      }
    }
  }

  private hasSelectedDescendant(item: DropdownItem): boolean {
    if (!item.hasChildren()) {
      return false;
    }
    return item.children!.some(child => 
      child.selected || this.hasSelectedDescendant(child)
    );
  }

  selectPredefinedDateRange(range: string) {
    const today = this.calendar.getToday();
    this.selectedDateRange = range;
    let fromDate: NgbDate;
    let toDate: NgbDate;
    
    switch (range) {
        case DateRangeType.LAST_7_DAYS:
            fromDate = this.calendar.getNext(this.calendar.getPrev(today, 'd', 7), 'd', 1);
            toDate = today;
            break;
        case DateRangeType.LAST_14_DAYS:
            fromDate = this.calendar.getNext(this.calendar.getPrev(today, 'd', 14), 'd', 1);
            toDate = today;
            break;
        case DateRangeType.LAST_30_DAYS:
            fromDate = this.calendar.getPrev(today, 'd', 30);
            toDate = today;
            break;
        case DateRangeType.LAST_3_MONTHS:
        case DateRangeType.QUARTER_TO_DATE:
            fromDate = this.calendar.getNext(this.calendar.getPrev(today, 'm', 3), 'd', 1);
            toDate = today;
            break;
        case DateRangeType.YEAR_TO_DATE:
            fromDate = this.calendar.getNext(this.calendar.getPrev(today, 'y', 1), 'd', 1);
            toDate = today;
            break;
        case DateRangeType.TODAY:
        default:
            fromDate = today;
            toDate = today;
            break;
    }
    
    this.dateFromFilter = fromDate;
    this.dateToFilter = toDate;
    
    let leftCalendarNav: NgbDate;
    let rightCalendarNav: NgbDate;
    
    if (range === DateRangeType.LAST_3_MONTHS || range === DateRangeType.QUARTER_TO_DATE || range === DateRangeType.YEAR_TO_DATE) {
      leftCalendarNav = fromDate;
      rightCalendarNav = toDate;
    } else {
      leftCalendarNav = this.calendar.getPrev(today, 'm', 1);
      rightCalendarNav = today;
    }
    
    this.leftCalendarDate = leftCalendarNav;
    this.rightCalendarDate = rightCalendarNav;
    
    setTimeout(() => {
      if (this.leftDatePicker) {
        this.leftDatePicker.navigateTo(leftCalendarNav);
      }
      if (this.rightDatePicker) {
        this.rightDatePicker.navigateTo(rightCalendarNav);
      }
      this.cdr.detectChanges();
    }, 0);
  }

  onDateSelection(date: NgbDate) {
    if (!this.dateFromFilter && !this.dateToFilter) {
      this.dateFromFilter = date;
    } else if (this.dateFromFilter && !this.dateToFilter && !date.before(this.dateFromFilter)) {
      const oneYearFromStart = this.calendar.getPrev(this.calendar.getNext(this.dateFromFilter, 'y', 1), 'd', 1);
      if (date.after(oneYearFromStart)) {
        this.dateToFilter = oneYearFromStart;
      } else {
        this.dateToFilter = date;
      }
    } else {
      this.dateFromFilter = date;
      this.dateToFilter = null;
    }
    this.selectedDateRange = '';
  }

  isMiddleDate = (date: NgbDate) => this.dateFromFilter && this.dateToFilter && date.after(this.dateFromFilter) && date.before(this.dateToFilter);
  isStartDate = (date: NgbDate) => date.equals(this.dateFromFilter);
  isEndDate = (date: NgbDate) => date.equals(this.dateToFilter);
  
  isValidDateRange = () => {
    if (!this.dateFromFilter || !this.dateToFilter) {
      return false;
    }
    
    if (this.dateToFilter.before(this.dateFromFilter)) {
      return false;
    }
    
    const oneYearFromStart = this.calendar.getPrev(this.calendar.getNext(this.dateFromFilter, 'y', 1), 'd', 1);
    return !this.dateToFilter.after(oneYearFromStart);
  };

  markDisabled = (date: NgbDateStruct) => {
    const ngbDate = NgbDate.from(date);
    if (!ngbDate) return false;
    if (this.dateFromFilter && !this.dateToFilter) {
      const oneYearFromStart = this.calendar.getPrev(this.calendar.getNext(this.dateFromFilter, 'y', 1), 'd', 1);
      return ngbDate.after(oneYearFromStart);
    }
    return false;
  };

  hoveredDate: NgbDate | null = null;

  onDateHover(date: NgbDate) {
    this.hoveredDate = date;
  }

  getDateRangeDisplayText(): string {
    if (this.selectedDateRange) {
      const option = this.dateRangeOptions.find(opt => opt.value === this.selectedDateRange);
      return option ? option.label : 'Select Value';
    }
    if (this.dateFromFilter && this.dateToFilter) {
      return `${this.formatDate(this.dateFromFilter)} - ${this.formatDate(this.dateToFilter)}`;
    }
    return 'Select Value';
  }

  private formatDate(date: NgbDate): string {
    return `${date.day}/${date.month}/${date.year}`;
  }

  onLeftCalendarNavigate(event: any) {
    const year = event.next?.year || event.year;
    const month = event.next?.month || event.month;
    this.leftCalendarDate = new NgbDate(year, month, 1);
    if (this.rightCalendarDate && this.isDateBefore(this.rightCalendarDate, this.leftCalendarDate)) {
      this.rightCalendarDate = this.calendar.getNext(this.leftCalendarDate, 'm', 1);
    }
    this.leftCalendarMinDate = this.leftCalendarDate;
  }

  onRightCalendarNavigate(event: any) {
    const year = event.next?.year || event.year;
    const month = event.next?.month || event.month;
    const newRightDate = new NgbDate(year, month, 1);
    if (this.leftCalendarDate && !this.isDateBefore(newRightDate, this.leftCalendarDate)) {
      this.rightCalendarDate = newRightDate;
    } else if (this.leftCalendarDate) {
      this.rightCalendarDate = this.calendar.getNext(this.leftCalendarDate, 'm', 1);
    }
  }

  private isDateBefore(date1: NgbDate, date2: NgbDate): boolean {
    if (date1.year !== date2.year) {
      return date1.year < date2.year;
    }
    if (date1.month !== date2.month) {
      return date1.month < date2.month;
    }
    return date1.day < date2.day;
  }

  applyCurrentFilter() {
    if (!this.filterField) return;

    let filterValue: any = null;
    switch(this.filterField) {
      case FilterField.DATE:
        if (this.dateFromFilter && this.dateToFilter) {
          filterValue = { from: this.dateFromFilter, to: this.dateToFilter };
        }
        break;
      case FilterField.STATUS:
        filterValue = new Set(this.dropdownFilterValues);
        break;
      case FilterField.MERCHANT_ID:
      case FilterField.MERCHANT_NAME:
        filterValue = this.textInputFilter;
        break;
    }
    
    if (filterValue) {
      if (filterValue instanceof Set && filterValue.size === 0) {
        this.filters.delete(this.filterField);
      } else {
        this.filters.set(this.filterField, filterValue);
      }
    } else {
      this.filters.delete(this.filterField);
    }
    
    this.popupManager.closeAll();
    this.fetchIPWhitelist();
  }
  
  exportIPWhitelist() {
    const exportedColumns = this.columnDefs
      .filter(col => !col.hide && (col as any).colId !== 'actions')
      .filter(col => col.field !== undefined && col.headerName)
      .map(col => ({
        key: col.field!,
        label: col.headerName!
      }));

    this.apiControllerService.exportAllIPWhitelist(this.filters, exportedColumns).subscribe({
      next: (resp: any) => {
        if(resp?.fileName) {
          this.pollFileStatus(resp.fileName);
        }
      }, error: (error: HttpErrorResponse) => {
        console.error('Error exporting IP whitelist:', error);
        this.showDownloadUnsuccessfulDialog();
      }
    });
  }

  private pollFileStatus(fileName: string) {
    const maxPollingTime = maxWaitingTimeForDownload;
    const pollingInterval = 3000; // 3 seconds
    const startTime = Date.now();

    const poll = () => {
      // Check if we've exceeded the maximum polling time
      if (Date.now() - startTime > maxPollingTime) {
        console.warn('File export polling timeout after 30 seconds');
        this.showDownloadUnsuccessfulDialog();
        return;
      }

      this.apiControllerService.checkFileStatusAndGetDownloadLink(fileName).subscribe({
        next: (resp: any) => {
          if (resp?.status === 'ready') {
            // File is ready, handle download (will show success/failure dialog)
            this.handleFileDownload(resp);
          } else {
            // File not ready, continue polling
            setTimeout(poll, pollingInterval);
          }
        },
        error: (error: HttpErrorResponse) => {
          if(error.status === 404) {
            // File not ready, continue polling
            setTimeout(poll, pollingInterval);
          } else {
            console.error('Error checking file status:', error);
            // Don't continue polling on error to avoid multiple error dialogs
            this.showDownloadUnsuccessfulDialog();
          }
        }
      });
    };
    // Start polling
    poll();
  }

  private handleFileDownload(fileData: any) {
    // Handle the file download logic here
    if (fileData.downloadUrl) {
      try {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = fileData.downloadUrl;
        link.download = fileData.fileName || 'export';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up after a short delay to allow browser to start download
        setTimeout(() => {
          link.remove();
        }, 100);
        
        // Download triggered successfully
        this.showDownloadSuccessfulDialog();
      } catch (error) {
        console.error('Error triggering file download:', error);
        this.showDownloadUnsuccessfulDialog();
      }
    } else {
      console.error('No download URL provided');
      this.showDownloadUnsuccessfulDialog();
    }
  }

  private showDownloadSuccessfulDialog(): void {
    this.alertService.customInfoPopup({
        icon: 'assets/img/Tick.svg',
        title: 'Download Started',
        description: 'Your download has been initiated. Please check your browser\'s download folder.',
        primaryButton: {
            text: 'Close'
        }
    });
  }

  private showDownloadUnsuccessfulDialog(isDetailExport: boolean = false): void {
    this.alertService.customInfoPopup({
        icon: 'assets/img/failure.svg',
        title: 'Download Unsuccessful',
        description: 'IP whitelist failed to download. Try Again.',
        primaryButton: {
            text: 'Close'
        }
    });
  }

  get showCustomize(): boolean {
    return this.popupManager.isOpen(PopupType.CUSTOMIZE);
  }
  
  toggleCustomizeDropdown() {
    this.popupManager.toggle(PopupType.CUSTOMIZE);
    this.perfectScrollbarService.initializeScrollableList();
  }
  
  get filteredColumns(): ColDef[] {
    const q = (this.customizeSearch || '').trim().toLowerCase();
    return this.columnDefs.filter(col => {
      if ((col as any).colId === 'actions') return false;
      if (this.showSelectedFieldsOnly && col.hide) return false;
      if (!q) return true;
      const label = (col.headerName || col.field || '').toString().toLowerCase();
      return label.includes(q);
    });
  }
  
  customizeSearch = '';
  showSelectedFieldsOnly = false;
  
  selectAllColumns() {
    this.columnDefs = this.columnDefs.map(col => ({ ...col, hide: false }));
    
    if (this.gridApi) {
      const colIds = this.columnDefs.map(col => (col as any).colId).filter(Boolean);
      this.gridApi.setColumnsVisible(colIds, true);
    }
  }
  
  deselectAllColumns() {
    this.columnDefs = this.columnDefs.map(col => {
      if ((col as any).colId === 'actions') {
        return col;
      }
      return { ...col, hide: true };
    });
    
    if (this.gridApi) {
      const colIds = this.columnDefs.map(col => (col as any).colId).filter(colId => colId && colId !== 'actions');
      this.gridApi.setColumnsVisible(colIds, false);
    }
  }
  
  toggleShowSelectedFieldsOnly() {
    this.showSelectedFieldsOnly = !this.showSelectedFieldsOnly;
  }
  
  onToggleColumnById(colId: string, checked: boolean) {
    const idx = this.getColumnIndexById(colId);
    if (idx === -1) return;
    const updated = [...this.columnDefs];
    const target = { ...updated[idx], hide: !checked } as ColDef;
    updated[idx] = target;
    this.columnDefs = updated;
    
    if (this.gridApi) {
      this.gridApi.setColumnsVisible([colId], checked);
    }
  }
  
  private getColumnIndexById(colId: string): number {
    return this.columnDefs.findIndex(c => (c as any).colId === colId);
  }

  trackByFilter(index: number, item: [FilterField, any]): FilterField {
    return item[0];
  }
  
  getFiltersArray(): [FilterField, any][] {
    return Array.from(this.filters.entries());
  }
  
  getFilterDisplayText(filterKey: FilterField, filterValue: any): string {
    switch(filterKey) {
      case FilterField.DATE:
        if (filterValue?.from && filterValue?.to) {
          const fromDate = `${filterValue.from.day}/${filterValue.from.month}/${filterValue.from.year}`;
          const toDate = `${filterValue.to.day}/${filterValue.to.month}/${filterValue.to.year}`;
          return `Date: ${fromDate} - ${toDate}`;
        }
        return 'Date: Invalid Range';
      
      case FilterField.STATUS:
        if (filterValue instanceof Set && filterValue.size > 0) {
          const values = Array.from(filterValue);
          if (values.length === 1) {
            return `${filterKey}: ${values[0]}`;
          } else {
            return `${filterKey}: ${values.length} selected`;
          }
        }
        return `${filterKey}: None`;
      
      case FilterField.MERCHANT_ID:
      default:
        return `${filterKey}: ${filterValue || ''}`;
    }
  }

  clearAllFilters() {
    this.initFilterValues();
    this.filters.clear();
    this.fetchIPWhitelist();
  }

  removeFilter(filterKey: FilterField) {
    this.initFilterValues();
    this.filters.delete(filterKey);
    this.fetchIPWhitelist();
  }

  getItemRangeInPage(): string {
    if(this.totalItems === 0) {
      return '0';
    }
    let start = ((this.currentPage-1) * this.rowsPerPage)+ 1;
    let end = Math.min(((start - 1) + (this.currentPage === 1 ? this.rowsPerPage : ((this.currentPage - 1) * this.rowsPerPage))), this.totalItems);
    return `${start} - ${end}`;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.fetchIPWhitelist();
    if (this.gridApi) {
      this.gridApi.paginationGoToPage(page - 1);
    }
  }

  changeRowsPerPage(value: number) {
    this.rowsPerPage = Number(value);
    this.currentPage = 1;
    this.fetchIPWhitelist();
    if (this.gridApi) {
      this.gridApi.setGridOption('paginationPageSize', this.rowsPerPage);
      this.gridApi.paginationGoToFirstPage();
    }
  }

  // ========== History Panel Methods ==========
  openHistoryPanel() {
    this.showHistoryPanel = true;
    // reset
    this.historyItems = [];
    this.historyPageNumber = 0;
    this.historyHasMore = true;
    this.historyTotalItems = 0;
    // defer fetch to ensure panel rendered
    setTimeout(() => {
      if (this.historyScrollContainer?.nativeElement) {
        this.historyScrollContainer.nativeElement.scrollTop = 0;
      }
      this.fetchHistoryPage();
    }, 0);
  }

  closeHistoryPanel() {
    this.showHistoryPanel = false;
  }

  onHistoryBackdrop(ev: MouseEvent) {
    // Close when clicking outside the panel
    this.closeHistoryPanel();
  }

  onHistoryScroll() {
    if (!this.historyScrollContainer) return;
    const el = this.historyScrollContainer.nativeElement;
    const threshold = 120; // px from bottom to prefetch next page
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.fetchHistoryPage();
    }
  }

  private fetchHistoryPage() {
    if (this.historyLoading || !this.historyHasMore) return;
    this.historyLoading = true;

    const shouldGateFetch = this.role === Role.PAY10_ADMIN && this.hasMerchantFilter;
    const merchantIdValue = this.filters.get(FilterField.MERCHANT_ID);
    console.log("shouldGateFetch: ", shouldGateFetch, ", merchantIdValue= ", merchantIdValue);
    this.apiControllerService
      .getIPWhitelistHistory(this.historyPageSize, this.historyPageNumber, shouldGateFetch && merchantIdValue ? merchantIdValue : undefined)
      .subscribe({
        next: (resp: any) => {
          const content = resp?.content || resp?.items || [];
          const total = resp?.totalElements ?? resp?.total ?? (this.historyItems.length + content.length);
          this.historyItems = [...this.historyItems, ...content];
          this.historyTotalItems = total;
          this.historyPageNumber += 1;
          this.historyHasMore = this.historyItems.length < total && content.length > 0;
          this.historyLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          console.error('getIPWhitelistHistory failed:', error);
          this.historyLoading = false;
          this.historyHasMore = false;
          this.alertService.customInfoPopup({
            icon: 'assets/img/failure.svg',
            title: 'IP whitelist history Fetching Failed',
            description: `${parseHttpErrorMessage(error)}.${error.status ===403 ? '' : ' Please try again later.'}`,
            primaryButton: {
              text: 'OK'
            }}
          );
        }
      });
  }

  getHistoryTypeLabel(item: any): string {
    // Use API's eventAction directly
    return (item?.eventAction || '').toString();
  }

  getHistoryMessageHTML(item: any): SafeHtml {
    const raw = (item?.eventMessage || '').toString();
    // Replace every left side before an arrow with <s>left</s>→, including after commas (optional spaces)
    const replaced = raw.replace(/(^|,\s*)([^→,]+)→/g, (_m: string, sep: string, from: string) => `${sep}<s>${from}</s> → `);
    return this.sanitizer.bypassSecurityTrustHtml(replaced);
  }

  formatHistoryTime(item: any): string {
    const dt = item?.eventTime;
    if (!dt) return '';
    try {
      const d = new Date(dt);
      if (!Number.isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} of ${hh}:${min}`;
      }
    } catch {}
    return dt.toString();
  }

  getHistoryActor(item: any): string {
    return item?.modifiedBy || '';
  }

  exportHistory() {
    const columns = [
      { key: 'eventAction', label: 'Action' },
      { key: 'eventMessage', label: 'Change' },
      { key: 'eventTime', label: 'DateTime' },
      { key: 'modifiedBy', label: 'Modified By' }
    ]
    this.apiControllerService.exportConfigtHistory('whitelist-ip-history', columns).subscribe({
      next: (resp: any) => {
        if(resp?.fileName) {
          this.pollFileStatus(resp.fileName);
        }
      }, error: (error: HttpErrorResponse) => {
        console.error('Error exporting IP whitelist history:', error);
        this.showDownloadUnsuccessfulDialog();
      }
    });
  }
}